import { useRef, useState } from "react";
import { Bold, Italic, Image as ImageIcon, Link as LinkIcon, List, Eye } from "lucide-react";
import { RichText } from "@/components/RichText";
import { htmlToMarkdown } from "@/lib/html-to-markdown";

type Props = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
};

/**
 * Plain textarea plus a toolbar that writes Markdown at the cursor, with a live
 * preview using the exact renderer the public page uses — so what a host sees
 * here is what a pilot sees there.
 */
export function MarkdownField({ value, onChange, placeholder, rows = 6, className = "" }: Props) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [showPreview, setShowPreview] = useState(true);

  function apply(transform: (selected: string) => string, fallbackSelection = "text") {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || fallbackSelection;
    const inserted = transform(selected);
    const next = value.slice(0, start) + inserted + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + inserted.length);
    });
  }

  function wrapLines(prefix: string) {
    apply((s) =>
      s
        .split("\n")
        .map((line) => `${prefix}${line}`)
        .join("\n"),
    );
  }

  /**
   * Paste from Google Docs / Word / the web keeps headings, bold, italic,
   * lists and links by converting the clipboard's HTML flavour to Markdown.
   * Plain-text pastes fall through to the browser default.
   */
  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const html = e.clipboardData.getData("text/html");
    if (!html?.trim()) return;
    const markdown = htmlToMarkdown(html);
    if (!markdown) return;
    e.preventDefault();
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + markdown + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + markdown.length, start + markdown.length);
    });
  }

  const btn =
    "inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted";

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        <button type="button" className={btn} onClick={() => wrapLines("# ")} title="Big heading">
          H1
        </button>
        <button type="button" className={btn} onClick={() => wrapLines("## ")} title="Heading">
          H2
        </button>
        <button
          type="button"
          className={btn}
          onClick={() => wrapLines("### ")}
          title="Small heading"
        >
          H3
        </button>
        <button type="button" className={btn} onClick={() => apply((s) => `**${s}**`)} title="Bold">
          <Bold className="size-3.5" />
        </button>
        <button type="button" className={btn} onClick={() => apply((s) => `*${s}*`)} title="Italic">
          <Italic className="size-3.5" />
        </button>
        <button type="button" className={btn} onClick={() => wrapLines("- ")} title="Bullet list">
          <List className="size-3.5" />
        </button>
        <button
          type="button"
          className={btn}
          title="Link"
          onClick={() => {
            const url = window.prompt("Paste the link address");
            if (!url) return;
            apply((s) => `[${s}](${url})`, "link text");
          }}
        >
          <LinkIcon className="size-3.5" />
        </button>
        <button
          type="button"
          className={btn}
          title="Image or meme link"
          onClick={() => {
            const url = window.prompt("Paste the image or GIF address");
            if (!url) return;
            apply(() => `\n![](${url})\n`);
          }}
        >
          <ImageIcon className="size-3.5" /> Meme
        </button>
        <button
          type="button"
          className={`${btn} ml-auto`}
          onClick={() => setShowPreview((v) => !v)}
        >
          <Eye className="size-3.5" /> {showPreview ? "Hide preview" : "Preview"}
        </button>
      </div>

      <textarea
        ref={ref}
        value={value}
        rows={rows}
        placeholder={placeholder}
        onPaste={handlePaste}
        onChange={(e) => onChange(e.target.value)}

        className={className}
      />

      {showPreview && value.trim() && (
        <div className="rounded-xl border border-border bg-muted/30 p-3">
          <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Preview</p>
          <RichText value={value} />
        </div>
      )}
    </div>
  );
}

export default MarkdownField;
